import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { catchError, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, throwError } from 'rxjs';

// Service
import { WidgetsService } from './widgets.service';

// Helpers
import { UserHelper } from '@helpers/user.helper';

// Store
import { loadProfileData } from '@store/profile/profile.actions';
import { loadPaymentCards } from '@store/payments/payments.actions';
import { loadCallHistory } from '@store/call-history/call-history.actions';
import { loadLanguagesList } from '@store/languages/languages.actions';
import { loadTimezones } from '@store/timezones/timezones.actions';

// Interfaces
import { IUser, ITemporaryUser } from '@interfaces/user.interface';

// Constants
import { LOCAL_STORAGE_KEYS } from '@constants/storage.constants';
import { USER_TYPE_URL } from '@constants/user-type-url.constants';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private httpClient = inject(HttpClient);
  private store = inject(Store);
  private widgetsService = inject(WidgetsService);

  public isAuthUser$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public login(data: { device_UDID: string; email: string; password: string }): Observable<IUser> {
    const isWidget = this.widgetsService.getWidgetStatus();
    const widgetData = isWidget ? { ...data, widget_code: this.widgetsService.widgetId } : data;
    return this.httpClient.post<IUser>(`${USER_TYPE_URL.USER}login`, widgetData).pipe(
      catchError(error => throwError(error)),
      tap(data => {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.DEEPLINK_TOKEN);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.TEMP_TOKEN);
        UserHelper.saveUser(data);
        localStorage.setItem(LOCAL_STORAGE_KEYS.USER_ID, data.user_id.toString());
        this.store.dispatch(loadProfileData());
        this.store.dispatch(loadCallHistory({ page: 0, isLoadMore: false }));
        this.store.dispatch(loadPaymentCards());
        this.store.dispatch(loadLanguagesList());
        this.store.dispatch(loadTimezones());
        this.isAuthUser$.next(true);
      }),
    );
  }

  public loginTemporary(device_UDID: string): Observable<ITemporaryUser> {
    return this.httpClient
      .post<ITemporaryUser>(`${USER_TYPE_URL.TEMPORARY}sign-up`, { device_UDID })
      .pipe(
        catchError(error => throwError(error)),
        tap(data => {
          localStorage.removeItem(LOCAL_STORAGE_KEYS.DEEPLINK_TOKEN);
          localStorage.removeItem(LOCAL_STORAGE_KEYS.TEMP_TOKEN);
          localStorage.setItem(LOCAL_STORAGE_KEYS.USER_ID, data.user_id.toString());
        }),
      );
  }

  loginByEmail(data: { email: string }): Observable<{ status: string; message: string }> {
    const isWidget = this.widgetsService.getWidgetStatus();
    const widgetData = isWidget ? { ...data, widget_code: this.widgetsService.widgetId } : data;
    return this.httpClient.post<{ status: string; message: string }>(
      `${USER_TYPE_URL.USER}login-by-email`,
      widgetData,
    );
  }

  loginByPhone(data: { phone: string }): Observable<{ status: string; message: string }> {
    const isWidget = this.widgetsService.getWidgetStatus();
    const widgetData = isWidget ? { ...data, widget_code: this.widgetsService.widgetId } : data;
    return this.httpClient.post<{ status: string; message: string }>(
      `${USER_TYPE_URL.USER}login-by-phone`,
      widgetData,
    );
  }

  public register(data: {
    device_UDID: string;
    email: string;
    password: string;
    password_confirmation: string;
  }): Observable<IUser> {
    return this.httpClient.post<IUser>(`${USER_TYPE_URL.USER}sign-up`, data);
  }

  public resendEmail(data: { mail: string }): Observable<{ status: string; message: string }> {
    return this.httpClient.patch<{ status: string; message: string }>(
      `${USER_TYPE_URL.USER}resend-activation-email`,
      data,
    );
  }

  public forgotPass(data: {
    email: string;
    password: string;
    password_confirmation: string;
  }): Observable<{ status: string; message: string }> {
    return this.httpClient.post<{ status: string; message: string }>(
      `${USER_TYPE_URL.USER}forgot-password`,
      data,
    );
  }

  public resendForgotPasswordEmail(email: string): Observable<{ status: string; message: string }> {
    return this.httpClient.post<{ status: string; message: string }>(
      `${USER_TYPE_URL.USER}resend-forgot-password-email`,
      { email },
    );
  }

  public logoutUser(): Observable<{ status: string; message: string }> {
    return this.httpClient
      .post<{ status: string; message: string }>(`${USER_TYPE_URL.USER}logout`, {})
      .pipe(
        catchError(error => throwError(error)),
        tap(() => {
          this.isAuthUser$.next(false);
        }),
      );
  }
}
